'use client';

import Link from 'next/link';

import { useLandingProductGTM } from '@/fo/api/gtm';
import AddToCartIcon from '@/fo/icons/base/landing/add-to-cart.svg';
import DayIcon from '@/fo/icons/base/landing/day.svg';
import EyeIcon from '@/fo/icons/base/landing/eye.svg';
import HandIcon from '@/fo/icons/base/landing/hand.svg';
import LipIcon from '@/fo/icons/base/landing/lip.svg';
import ListItemIcon from '@/fo/icons/base/landing/list-icon.svg';
import NightIcon from '@/fo/icons/base/landing/night.svg';
import { MappedProduct } from '@/fo/util/map-product-data';
import { reachMetrikaGoal } from '@/fo/util/metrics';

import { styles } from './UiLandingProductCardBioderma.styles';

export interface Product {
  label?: string;
  labelColor?: string;
  extraLabel?: string;
  extraLabelColor?: string;
  heading?: string | React.ReactNode;
  text?: string | React.ReactNode;
  labels?: string[];
  list?: React.ReactNode[];
  image: {
    sm?: string;
    md?: string;
  };
  basePrice?: number;
  finalPrice?: number | null;
  sku?: string;
  name?: string;
  brand?: string;
  series?: string;
  link?: string;
  volume?: string;
  baseImage?: { src: string; alt: string } | null;
  configurableVariants?: MappedProduct['configurableVariants'];
}

export interface UiLandingProductCardBiodermaProps {
  className?: string;
  classNameWrapper?: string;
  classNameImageWrapper?: string;
  classNameList?: string;
  variant?: 'default' | 'secondary';
  listItemVariant?: 'icon' | 'dot';
  onAddToCart?: () => void;
  product: Product;
  positionIndex?: number;
}

export function UiLandingProductCardBioderma({
  className,
  classNameWrapper,
  classNameImageWrapper,
  classNameList,
  product,
  onAddToCart,
  variant = 'default',
  listItemVariant = 'icon',
  positionIndex = 0
}: UiLandingProductCardBiodermaProps) {
  const slots = styles({ variant });
  const { ref, onSelectItem, viewItemList } = useLandingProductGTM(
    product,
    positionIndex
  );

  const onProductClick = () => {
    onSelectItem();
    reachMetrikaGoal('perehod_v_kartochku_tovara');
  };

  return (
    <div
      ref={ref}
      className={slots.base({ className })}
    >
      {viewItemList}
      {product.labels && (
        <div className={slots.labels()}>
          {product.labels.map((label) => (
            <div key={label}>
              {label === 'day' && <DayIcon />}
              {label === 'night' && <NightIcon />}
              {label === 'hand' && <HandIcon />}
              {label === 'lip' && <LipIcon />}
              {label === 'eye' && <EyeIcon />}
            </div>
          ))}
        </div>
      )}
      <Link
        href={product.link || '#'}
        target="_blank"
        onClick={onProductClick}
        className={slots.imageWrapper({ className: classNameImageWrapper })}
      >
        <picture>
          <source
            srcSet={product.image.sm}
            media="(max-width: 767px)"
          />
          <img
            src={product.image.md}
            alt={typeof product.heading === 'string' ? product.heading : ''}
            className={slots.image()}
          />
        </picture>
      </Link>

      <div className={slots.wrapper({ className: classNameWrapper })}>
        <div className={slots.labelRow()}>
          <p
            className={slots.label()}
            style={{
              backgroundColor: product.labelColor,
              opacity: product.label ? 1 : 0
            }}
          >
            {product.label}
          </p>
          {product.extraLabel && (
            <p
              className={slots.label()}
              style={{ backgroundColor: product.extraLabelColor }}
            >
              {product.extraLabel}
            </p>
          )}
        </div>
        <Link
          href={product.link || '#'}
          target="_blank"
          onClick={onProductClick}
          className={slots.heading()}
          dangerouslySetInnerHTML={{ __html: product.heading as string }}
        />
        <div className={slots.content()}>
          {product.text && (
            <Link
              className={slots.text()}
              href={product.link || '#'}
              target="_blank"
              onClick={onProductClick}
            >
              {product.text}
            </Link>
          )}
          {product.list && (
            <ul className={slots.list({ className: classNameList })}>
              {product.list.map((item, index) => (
                <li
                  key={index}
                  className={slots.listItem()}
                >
                  {listItemVariant === 'icon' ? (
                    <ListItemIcon className={slots.listItemIcon()} />
                  ) : (
                    <span className={slots.listItemDot()} />
                  )}
                  {item}
                </li>
              ))}
            </ul>
          )}

          {product.basePrice && (
            <div className={slots.action()}>
              <div className={slots.priceWrapper()}>
                <div className={slots.price()}>
                  <span className={slots.priceValue()}>
                    {Number(
                      product.finalPrice ?? product.basePrice
                    ).toLocaleString('ru-RU')}
                  </span>
                  <span className={slots.priceCurrency()}>₽</span>
                </div>
                {product.finalPrice &&
                  product.finalPrice !== product.basePrice && (
                    <div className={slots.priceOld()}>
                      {Number(product.basePrice).toLocaleString('ru-RU')} ₽
                    </div>
                  )}
              </div>

              <button
                className={slots.button()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  onAddToCart?.();
                  reachMetrikaGoal('dobavlenie_v_korzinu');
                }}
              >
                <AddToCartIcon />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
